import Lenis from 'lenis';
import { gsap, ScrollTrigger } from './gsapSetup';
import { prefersReducedMotion } from './reducedMotion';

let lenis: Lenis | null = null;

/**
 * Initialise Lenis smooth scrolling and drive it from the GSAP ticker so ScrollTrigger and
 * Lenis stay perfectly in sync. Disabled entirely under prefers-reduced-motion (native scroll).
 * Returns a cleanup function. Mounted ONLY on the public site (SiteLayout), never the dashboard.
 */
export function initSmoothScroll(): () => void {
  if (prefersReducedMotion()) return () => {};
  if (lenis) return () => {};

  lenis = new Lenis({
    duration: 1.1,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    touchMultiplier: 1.5,
  });

  lenis.on('scroll', ScrollTrigger.update);

  const raf = (time: number) => {
    lenis?.raf(time * 1000);
  };
  gsap.ticker.add(raf);
  gsap.ticker.lagSmoothing(0);

  return () => {
    gsap.ticker.remove(raf);
    lenis?.destroy();
    lenis = null;
  };
}

export function getLenis(): Lenis | null {
  return lenis;
}

/** Smoothly scroll to a target (selector, element or offset). Falls back to native. */
export function scrollTo(target: string | number | HTMLElement, opts?: { offset?: number }): void {
  if (lenis) {
    lenis.scrollTo(target as never, { offset: opts?.offset ?? 0 });
    return;
  }
  if (typeof target === 'number') {
    window.scrollTo({ top: target, behavior: 'smooth' });
  } else if (typeof target !== 'string') {
    target.scrollIntoView({ behavior: 'smooth' });
  } else {
    document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
  }
}

export function stopScroll(): void {
  lenis?.stop();
}
export function startScroll(): void {
  lenis?.start();
}
